import { EnvironmentInjector, inject, Injectable, runInInjectionContext } from '@angular/core';
import { FIRESTORE_SERVICE } from './firestore.service';
import { SessionStateService } from './session-state.service';
import { firstValueFrom } from 'rxjs';
import {
  type FirestoreOrgaUser,
  type FirestoreTimer,
  type IsoDate,
  mapOrgaUserSummary,
  type NullablePartial,
  buildEmbeddedTimerStopUpdate,
  buildLinkedTimerStopWorkUpdate,
  createClosedTimeEntry,
  type TimerEntity,
  type TimeEntry,
  workMinutesAfterTimeEntryUpdate,
  type WorkEntity2,
} from '@jessie/commons';
import { filter, first, map, switchMap } from 'rxjs/operators';
import {
  collection,
  deleteField,
  FieldPath,
  doc,
  type DocumentSnapshot,
  Firestore,
  getDocsFromServer,
  orderBy,
  runTransaction,
  type Transaction,
  where,
} from '@angular/fire/firestore';
import { addMinutes, formatISO, isValid, parseISO } from 'date-fns';

@Injectable({
  providedIn: 'root',
})
export class TimerService {
  private injector = inject(EnvironmentInjector);
  private rootFirestoreService = inject(FIRESTORE_SERVICE);
  private firestore = inject(Firestore);
  private runFirestoreTransaction = runTransaction;

  private inCtx<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  private sessionState = inject(SessionStateService);

  getTimer(timerId: string) {
    return this.sessionState.getOrgaUser().pipe(
      filter((orgaUser): orgaUser is FirestoreOrgaUser => !!orgaUser),
      first(),
      switchMap((orgaUser) =>
        this.rootFirestoreService.getDoc<TimerEntity>(orgaUser.docRef.path + `/timer/${timerId}`)
      )
    );
  }

  getTimers() {
    return this.sessionState.getOrgaUser().pipe(
      filter((orgaUser): orgaUser is FirestoreOrgaUser => !!orgaUser),
      first(),
      switchMap((orgaUser) =>
        this.rootFirestoreService.getDocs<TimerEntity>(orgaUser.docRef.path + '/timer')
      )
    );
  }

  getTimersByDate(date: IsoDate) {
    return this.sessionState.getOrgaUser().pipe(
      filter((orgaUser): orgaUser is FirestoreOrgaUser => !!orgaUser),
      first(),
      switchMap((orgaUser) =>
        this.rootFirestoreService.getDocs<TimerEntity>(
          orgaUser.docRef.path + '/timer',
          runInInjectionContext(this.injector, () => where('date', '==', date)),
          runInInjectionContext(this.injector, () => orderBy('createdAt', 'asc'))
        )
      )
    );
  }

  getActiveTimer() {
    return this.sessionState.getOrgaUser().pipe(
      filter((orgaUser): orgaUser is FirestoreOrgaUser => !!orgaUser),
      first(),
      switchMap((orgaUser: FirestoreOrgaUser) =>
        this.rootFirestoreService.getDocs<TimerEntity>(
          `${orgaUser.docRef.path}/timer/`,
          runInInjectionContext(this.injector, () => where('timerState', '==', 'active'))
        )
      ),
      map((timers) => {
        if (timers.length > 0) {
          return timers[0];
        } else {
          return null;
        }
      })
    );
  }

  getOutdatedTimers() {
    return this.sessionState.getOrgaUser().pipe(
      filter((orgaUser): orgaUser is FirestoreOrgaUser => !!orgaUser),
      first(),
      switchMap((orgaUser: FirestoreOrgaUser) =>
        this.rootFirestoreService.getDocs<TimerEntity>(
          `${orgaUser.docRef.path}/timer/`,
          runInInjectionContext(this.injector, () => where('timerState', '==', 'inactive')),
          runInInjectionContext(this.injector, () =>
            where('date', '<=', formatISO(new Date(), { representation: 'date' }))
          )
        )
      )
    );
  }

  expand(timer: FirestoreTimer) {
    return this.rootFirestoreService.updateDoc(timer.docRef.path, { isExpanded: true });
  }

  collapse(timer: FirestoreTimer) {
    return this.rootFirestoreService.updateDoc(timer.docRef.path, { isExpanded: false });
  }

  updateEmbeddedWork(timer: FirestoreTimer, work: NullablePartial<WorkEntity2>) {
    const firebaseUpdateObject = Object.entries(work).reduce(
      (prev, cur) => ({ ...prev, [`work.${cur[0]}`]: cur[1] }),
      {}
    );
    return this.rootFirestoreService.updateDoc(timer.docRef.path, firebaseUpdateObject);
  }

  async updateEmbeddedTimeEntry(
    timer: FirestoreTimer,
    sessionEntryId: string,
    nextEntry: TimeEntry
  ): Promise<void> {

    return this.inCtx(() =>
      this.runFirestoreTransaction(this.firestore, async (transaction) => {
        const timerDoc = await transaction.get(timer.docRef);
        if (!timerDoc.exists()) {
          throw new Error(`Timer with id ${timer.id} does not exist`);
        }

        const latestTimer = timerDoc.data() as TimerEntity;
        if (
          latestTimer.timerState !== 'inactive' ||
          latestTimer.workItemState !== 'embedded'
        ) {
          throw new Error(`Timer with id ${timer.id} cannot update a paused session`);
        }

        const latestSessionEntry = latestTimer.work.timeEntries?.[sessionEntryId];
        const expectedSessionEntry =
          timer.data.workItemState === 'embedded'
            ? timer.data.work.timeEntries?.[sessionEntryId]
            : undefined;
        if (
          !latestSessionEntry ||
          !expectedSessionEntry ||
          latestSessionEntry.startTime !== expectedSessionEntry.startTime ||
          latestSessionEntry.totalMinutes !== expectedSessionEntry.totalMinutes
        ) {
          throw new Error(`Time entry with id ${sessionEntryId} has changed`);
        }

        const originalStart = parseISO(latestSessionEntry.startTime);
        const originalEnd = addMinutes(originalStart, latestSessionEntry.totalMinutes);
        const nextStart = parseISO(nextEntry.startTime);
        if (
          !isValid(originalStart) ||
          !isValid(originalEnd) ||
          !isValid(nextStart) ||
          !Number.isFinite(latestSessionEntry.totalMinutes) ||
          latestSessionEntry.totalMinutes < 1 ||
          !Number.isFinite(nextEntry.totalMinutes) ||
          nextEntry.totalMinutes < 1
        ) {
          throw new Error(`Time entry with id ${sessionEntryId} is invalid`);
        }

        const originalDate = formatISO(originalStart, { representation: 'date' });
        const nextEnd = addMinutes(nextStart, nextEntry.totalMinutes);
        if (
          formatISO(originalEnd, { representation: 'date' }) !== originalDate ||
          !isValid(nextEnd) ||
          formatISO(nextStart, { representation: 'date' }) !== originalDate ||
          formatISO(nextEnd, { representation: 'date' }) !== originalDate
        ) {
          throw new Error(`Time entry with id ${sessionEntryId} must remain on the same day`);
        }

        const currentWorkMinutes = latestTimer.work.workMinutes;
        if (
          typeof currentWorkMinutes !== 'number' ||
          !Number.isFinite(currentWorkMinutes)
        ) {
          throw new Error(`Timer with id ${timer.id} has invalid work minutes`);
        }

        transaction.update(
          timer.docRef,
          new FieldPath('work', 'timeEntries', sessionEntryId),
          nextEntry,
          'work.workMinutes',
          workMinutesAfterTimeEntryUpdate(
            currentWorkMinutes,
            latestSessionEntry,
            nextEntry.totalMinutes
          )
        );
      })
    );
  }

  async updateActiveStartTime(timer: FirestoreTimer, startTime: TimeEntry['startTime']): Promise<void> {
    return this.inCtx(() =>
      this.runFirestoreTransaction(this.firestore, async (transaction) => {
        const timerDoc = await transaction.get(timer.docRef);
        if (!timerDoc.exists()) {
          throw new Error(`Timer with id ${timer.id} does not exist`);
        }

        const latestTimer = timerDoc.data() as TimerEntity;
        if (
          latestTimer.timerState !== 'active' ||
          latestTimer.workItemState !== 'embedded' ||
          timer.data.timerState !== 'active' ||
          timer.data.workItemState !== 'embedded' ||
          latestTimer.startTime !== timer.data.startTime
        ) {
          throw new Error(`Timer with id ${timer.id} cannot update its active start time`);
        }

        const now = new Date();
        const nextStart = parseISO(startTime);
        const today = formatISO(now, { representation: 'date' });
        if (
          !isValid(nextStart) ||
          latestTimer.date !== today ||
          formatISO(nextStart, { representation: 'date' }) !== latestTimer.date ||
          nextStart.getTime() >= now.getTime()
        ) {
          throw new Error(`Timer with id ${timer.id} has an invalid active start time`);
        }

        transaction.update(timer.docRef, { startTime });
      })
    );
  }

  async stopTimer() {
    const orgaUser = await firstValueFrom(
      this.sessionState
        .getOrgaUser()
        .pipe(filter((orgaUser): orgaUser is FirestoreOrgaUser => !!orgaUser))
    );

    const timersCollectionRef = runInInjectionContext(this.injector, () =>
      collection(this.firestore, `${orgaUser.docRef.path}/timer`)
    );
    const timersCollectionDocs = await runInInjectionContext(this.injector, () =>
      getDocsFromServer(timersCollectionRef)
    );

    return runInInjectionContext(this.injector, () =>
      runTransaction(this.firestore, async (transaction) => {
        const allTimers: Promise<DocumentSnapshot>[] = [] as Promise<DocumentSnapshot>[];
        timersCollectionDocs.forEach((doc) => {
          allTimers.push(transaction.get(doc.ref));
        });
        const fetchedTimers = await Promise.all(allTimers);

        await Promise.all(
          fetchedTimers.map((timer) => {
            const timerData = timer.data() as TimerEntity;
            if (timerData.timerState === 'active') {
              return this.transactionalStopTimer(
                transaction,
                timer as DocumentSnapshot<TimerEntity>
              );
            }
            return null;
          })
        );
      })
    );
  }

  async startTimer(timerId: string) {
    const orgaUser = await firstValueFrom(
      this.sessionState
        .getOrgaUser()
        .pipe(filter((orgaUser): orgaUser is FirestoreOrgaUser => !!orgaUser))
    );

    const timersCollectionRef = runInInjectionContext(this.injector, () =>
      collection(this.firestore, `${orgaUser.docRef.path}/timer`)
    );
    const timersCollectionDocs = await runInInjectionContext(this.injector, () =>
      getDocsFromServer(timersCollectionRef)
    );

    return runInInjectionContext(this.injector, () =>
      runTransaction(this.firestore, async (transaction) => {
        const allTimers: Promise<DocumentSnapshot>[] = [] as Promise<DocumentSnapshot>[];
        timersCollectionDocs.forEach((doc) => {
          allTimers.push(transaction.get(doc.ref));
        });
        const fetchedTimers = await Promise.all(allTimers);

        await Promise.all(
          fetchedTimers.map(async (timer) => {
            const timerData = timer.data() as TimerEntity;
            if (timerData.timerState === 'active' && timer.id !== timerId) {
              await this.transactionalStopTimer(
                transaction,
                timer as DocumentSnapshot<TimerEntity>
              );
            }
            if (timerData.timerState === 'inactive' && timer.id === timerId) {
              transaction.update(timer.ref, {
                timerState: 'active',
                startTime: new Date().toISOString(),
              });
            }
          })
        );
      })
    );
  }

  private async transactionalStopTimer(
    transaction: Transaction,
    runningTimer: DocumentSnapshot<TimerEntity>
  ) {
    // stop a running timer
    const runningTimerEntity: TimerEntity = runningTimer.data()!;
    if (runningTimerEntity.timerState === 'active') {
      const timeEntryId = crypto.randomUUID();
      const timeEntry = createClosedTimeEntry(runningTimerEntity.startTime);

      if (runningTimerEntity.workItemState === 'embedded') {
        transaction.update(
          runningTimer.ref,
          buildEmbeddedTimerStopUpdate(
            runningTimerEntity.work.timeEntries,
            runningTimerEntity.work.workMinutes,
            timeEntryId,
            timeEntry,
            runInInjectionContext(this.injector, () => deleteField())
          )
        );
      } else {
        const runningTimerWorkDoc = await transaction.get(
          runInInjectionContext(this.injector, () =>
            doc(this.firestore, runningTimerEntity.workPath)
          )
        );
        const runningTimerWorkEntity = runningTimerWorkDoc.data() as WorkEntity2;
        transaction
          .update(
            runningTimerWorkDoc.ref,
            buildLinkedTimerStopWorkUpdate(
              runningTimerWorkEntity.timeEntries,
              runningTimerWorkEntity.workMinutes,
              timeEntryId,
              timeEntry
            )
          )
          .update(runningTimer.ref, {
            timerState: 'inactive',
            startTime: deleteField(),
          });
      }
    }
  }

  async createTimer(date: IsoDate) {
    const orgaUser = await firstValueFrom(
      this.sessionState
        .getOrgaUser()
        .pipe(filter((orgaUser): orgaUser is FirestoreOrgaUser => !!orgaUser))
    );
    const orgaId = await firstValueFrom(this.sessionState.getOrgaId());
    const timer: TimerEntity = {
      createdAt: formatISO(new Date()),
      date: formatISO(parseISO(date), { representation: 'date' }),
      workItemState: 'embedded',
      timerState: 'inactive',
      work: {
        orgaId,
        orgaUser: mapOrgaUserSummary(orgaUser),
        customer: null,
        invoice: null,
        description: '',
        date: formatISO(parseISO(date), { representation: 'date' }),
        workMinutes: 0,
      },
    };
    const timerDoc = await this.rootFirestoreService.createDoc(
      orgaUser?.docRef.path + '/timer',
      timer
    );
    return timerDoc.id;
  }

  deleteTimer(timer: FirestoreTimer) {
    return this.rootFirestoreService.deleteDoc(timer.docRef.path);
  }
}
